/* eslint-disable unicorn/filename-case -- TODO rename later */
import {
  AuthenticationStore,
  captureAuthOwner,
  isStoredAccessTokenExpired,
  refreshAccessToken,
  removeAuthOwner,
  waitForAuthReady,
} from "@istari-frontend/auth";
import axios, { InternalAxiosRequestConfig } from "axios";

import { HttpStatusCodeType } from "../enums";

// ServiceConfig as AxiosRequestConfig.config
interface ServiceConfig {
  url?: string;
}

/**
 * Creates a service abstraction around the AxiosInstance.  It currently only exposes common axios HTTP methods, but this can be expanded in the future.
 * @param {*} configuration: simple object containing just the URL.  Can be made more configurable in the future.
 */
export default function createService(configuration?: ServiceConfig) {
  const instance = axios.create({
    baseURL: configuration?.url,
    timeout: 120000,
    withCredentials: true,
  });

  const requestOwners = new WeakMap<InternalAxiosRequestConfig, string>();
  instance.interceptors.request.use(async (config) => {
    if (!config.withCredentials) {
      return config as InternalAxiosRequestConfig;
    }
    if (isStoredAccessTokenExpired()) {
      await refreshAccessToken();
    }

    await waitForAuthReady();
    const owner = captureAuthOwner();
    const accessToken = AuthenticationStore().getAccessToken();
    if (owner) requestOwners.set(config, owner);

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config as InternalAxiosRequestConfig;
  });

  instance.interceptors.response.use(
    (response) => response?.data,

    async (error) => {
      if (error.response?.status === HttpStatusCodeType.Unauthorized) {
        await removeAuthOwner(requestOwners.get(error.config));
      }
      return Promise.reject(error);
    },
  );

  return {
    concurrentRequests: axios.all,
    get: instance.get,
    patch: instance.patch,
    post: instance.post,
    put: instance.put,
    remove: instance.delete,
  };
}
